<template>
  <details class="overflow-hidden rounded-lg border border-line bg-surface-2">
    <summary class="flex cursor-pointer select-none items-center gap-2 px-3 py-2 text-xs">
      <span class="flex size-5 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-700">
        <Wrench class="size-3" />
      </span>
      <span class="font-medium text-ink">{{ humanLabel }}</span>
      <span v-if="argHint" class="truncate text-faint">· {{ argHint }}</span>
      <code class="ml-auto shrink-0 font-mono text-[10px] text-faint">{{ block.name }}</code>
      <ChevronDown class="size-3.5 shrink-0 text-faint transition-transform [details[open]_&]:rotate-180" />
    </summary>
    <div class="border-t border-line p-2">
      <div class="relative">
        <pre class="max-h-48 overflow-auto rounded-md bg-code p-2.5 pr-14 font-mono text-[11px] leading-relaxed text-code-ink">{{ pretty }}</pre>
        <button
          type="button"
          class="absolute right-2 top-2 inline-flex items-center gap-1 rounded-md border border-white/15 bg-white/5 px-1.5 py-1 text-[11px] text-gray-300 hover:bg-white/10"
          :title="copied ? 'Copied' : 'Copy'"
          @click="copy"
        >
          <component :is="copied ? Check : Copy" class="size-3" />
        </button>
      </div>
    </div>
  </details>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { Wrench, ChevronDown, Copy, Check } from 'lucide-vue-next';
import type { TraceBlock } from '../../stores/chat';

const props = defineProps<{ block: TraceBlock }>();
const pretty = computed(() => JSON.stringify(props.block.input ?? {}, null, 2));

// Legible label: the tool name as words ("get design bindings"), plus the most
// salient argument so the activity reads ("query series · ser_power"). The raw
// name stays as a small mono tag.
const humanLabel = computed(() => (props.block.name ?? 'tool').replace(/_/g, ' '));
const argHint = computed(() => {
  const i = props.block.input as Record<string, unknown> | undefined;
  if (!i) return '';
  const keys = ['query', 'q', 'series_id', 'metric_name', 'design_id', 'doc_id', 'connector_id', 'name', 'id'];
  for (const k of keys) { const v = i[k]; if (typeof v === 'string' && v) return v.length > 40 ? v.slice(0, 40) + '…' : v; }
  return '';
});

const copied = ref(false);
async function copy(): Promise<void> {
  try {
    await navigator.clipboard.writeText(pretty.value);
    copied.value = true;
    setTimeout(() => (copied.value = false), 1500);
  } catch { /* clipboard blocked (insecure context) — ignore */ }
}
</script>
